import { useMemo } from 'react';

import { ExternalLink } from '@uikit';

import { LAUNCHPAD_SALE_POOL_STATUS, LaunchpadSalePool } from '@api';

import { NewTab } from '@uikit/icons/NewTab';

import BaseInfoTooltip from '@shared/common/components/BaseInfoTooltip';
import { formatNumberAsCurrency, formatNumberLocalized } from '@shared/helpers/formatters';

import { getNumberWithSymbol } from '@launchpad/components/pages/Portfolio/ui/PortfolioPool/PortfolioBasePool';
import { DepositFeeTooltip } from '@launchpad/components/ui/depositFee/DepositFeeTooltip';
import { useLaunchpadTotalDeposited } from '@launchpad/hooks/useLaunchpadTotalDeposited';
import { isProRataType } from '@launchpad/utils/contract';
import { UpcomingEventCountdown } from '@ui-uikit/lib/components/common/UpcomingEventCountdown';

import { getIsSaleCompletedPoolStatus, getPoolInfo } from './PoolCard';
import { PoolStat } from './PoolStatList';

const getAllocationFormat = (minAllocation: number, maxAllocation: number, ticker: string) => {
  if (ticker.toLocaleLowerCase() === 'usdt') {
    return `${minAllocation ? formatNumberAsCurrency({ amount: minAllocation }) : 'TBA'} - ${
      maxAllocation ? formatNumberAsCurrency({ amount: maxAllocation }) : 'TBA'
    }`;
  }

  return `${minAllocation ? formatNumberLocalized(minAllocation) : 'TBA'} - ${
    maxAllocation ? formatNumberLocalized(maxAllocation) : 'TBA'
  } ${ticker}`;
};

export const usePoolStats = ({
  pool,
  token,
  depositedAmount,
  fee,
}: {
  pool: LaunchpadSalePool;
  token: string;
  depositedAmount: number;
  fee?: number;
}) => {
  const { data: totalDepositedNumb } = useLaunchpadTotalDeposited({
    depositContractAddress: pool?.attributes.depositContractAddress,
    type: pool?.attributes.type,
  });
  const hasDepositStats = pool?.attributes.status !== LAUNCHPAD_SALE_POOL_STATUS.ANNOUNCED;
  const tokenTicker = pool?.attributes.depositTokenTicker || 'USDT';

  return useMemo<PoolStat[]>(() => {
    const poolInfo = getPoolInfo(pool);

    const poolSizeStatTooltip =
      tokenTicker === 'SQR'
        ? `Pool size indicating the amount of funds to be raised in SQR tokens. Each SQR token is valued at ${formatNumberAsCurrency(
            {
              amount: pool.attributes.depositTokenPrice || 0,
            },
          )}`
        : 'Pool size indicating the amount of funds to be raised.';

    const poolSizeStat: PoolStat = {
      id: 'pool-size',
      label: 'POOL SIZE',
      content: pool?.attributes?.goal
        ? getNumberWithSymbol(pool?.attributes?.goal, tokenTicker, true)
        : 'TBA',
      tooltip: <BaseInfoTooltip content={poolSizeStatTooltip} />,
    };

    const depositSizeStat: PoolStat = {
      id: 'deposit-size',
      label: 'Deposit Size',
      tooltip: <BaseInfoTooltip content="Deposit range per user." />,
      content: getAllocationFormat(
        pool.attributes.minAllocation,
        pool.attributes.maxAllocation,
        tokenTicker,
      ),
    };

    const tokenPrice =
      tokenTicker === 'USDT'
        ? pool?.attributes?.price
        : pool.attributes.depositTokenPrice * pool?.attributes?.price;

    const tokenPriceStat: PoolStat = {
      id: 'token-price',
      label: 'Token Price',
      tooltip: <BaseInfoTooltip content="Token price for this sale." />,
      content: (
        <>
          1 {token} = {tokenPrice ? getNumberWithSymbol(tokenPrice) : 'TBA'}
        </>
      ),
    };

    const typeStat: PoolStat = {
      id: 'type',
      label: 'Type',
      content: pool?.attributes?.meta?.whiteListExists ? poolInfo.whiteListName : poolInfo.typeName,
      tooltip: <BaseInfoTooltip content={poolInfo.tooltip} />,
    };

    const result: PoolStat[] = [
      poolSizeStat,
      ...(!isProRataType(pool.attributes.type) ? [depositSizeStat] : []),
      tokenPriceStat,
      typeStat,
    ];

    if (hasDepositStats) {
      const depositedPoolStat: PoolStat = {
        id: 'deposited',
        label: 'Deposited',
        tooltip: <BaseInfoTooltip content="Total amount deposited to the pool." />,
        content:
          pool.attributes.status !== 'SALE_STARTED' &&
          (pool?.attributes?.goal || 0) - (totalDepositedNumb || 0) < pool.attributes.minAllocation
            ? getNumberWithSymbol(pool?.attributes?.goal || 0 || 0, tokenTicker, true)
            : getNumberWithSymbol(totalDepositedNumb || 0, tokenTicker, true),
      };

      const selfDepositStat: PoolStat = {
        id: 'your-deposit',
        tooltip: (
          <DepositFeeTooltip
            className="ml-0"
            amount={depositedAmount}
            feeCalculatedValue={fee}
            currency={tokenTicker}
            poolType={pool.attributes.type}
          />
        ),
        label: 'My Deposit',
        content: getNumberWithSymbol(depositedAmount, tokenTicker, true),
      };

      result.push(depositedPoolStat, selfDepositStat);
    }

    if (getIsSaleCompletedPoolStatus(pool.attributes.status)) {
      const participantsStat: PoolStat = {
        id: 'participants',
        label: 'Participants',
        content: pool.attributes.participantCount,
        tooltip: (
          <BaseInfoTooltip content="Total amount of users who have already participated the sale." />
        ),
      };

      result.push(participantsStat);
    }

    if (
      pool.attributes.status !== LAUNCHPAD_SALE_POOL_STATUS.ANNOUNCED &&
      !!pool.attributes.depositContractAddress
    ) {
      const contractAddressStat: PoolStat = {
        id: 'contract-address',
        label: 'Deposit Contract',
        content: (
          <ExternalLink
            className="text-right"
            href={`https://bscscan.com/address/${pool.attributes.depositContractAddress}`}
          >
            <NewTab className="icon-sm text-primary-700" />
          </ExternalLink>
        ),
      };

      result.push(contractAddressStat);
    }

    if (pool.attributes.status === LAUNCHPAD_SALE_POOL_STATUS.SALE_STARTED) {
      const endCountdownStat: PoolStat = {
        id: 'pool-ends',
        label: 'Sale ends',
        tooltip: (
          <BaseInfoTooltip content="The countdown timer indicates the remaining time before the Pool sale ends. Make sure to participate before the timer reaches zero." />
        ),
        content: <UpcomingEventCountdown date={pool.attributes.endDate} />,
      };

      result.push(endCountdownStat);
    }

    return result;
  }, [pool, token, depositedAmount, hasDepositStats, totalDepositedNumb, tokenTicker]);
};
